// Renders V3 tool approval cards while keeping registry wiring separate.
import { ArrowRightLeft } from 'lucide-react';
import { Fragment, type ReactNode, useId, useRef, useState } from 'react';

import { type VariableTextEditorActions } from '@apps/web/components/shared/messages/variable_text_editor';
import { Input } from '@apps/web/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    type CommunicationCaregiver,
    useCommunicationCaregiverSearch,
} from '@apps/web/features/phoebe_chat_v3/api/caregiver_search';
import {
    draftIssue,
    draftIssueCopy,
    SMS_MESSAGE_CHARACTER_LIMIT,
    VOICE_INSTRUCTIONS_CHARACTER_LIMIT,
    VOICE_OPENING_LINE_CHARACTER_LIMIT,
} from '@apps/web/features/phoebe_chat_v3/model/draft_rules';
import {
    type ApprovalRenderer,
    approvalRenderer,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/approval_renderer';
import {
    AssignApprovalView,
    LegacyAssignmentApprovalView,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/assignment_approval_view';
import {
    communicationReceiptCopy,
    communicationReceiptState,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/communication_receipt';
import { EhrPersonNoteApprovalView } from '@apps/web/features/phoebe_chat_v3/registry/definitions/ehr_person_note_approval_view';
import { ShiftWriteApprovalView } from '@apps/web/features/phoebe_chat_v3/registry/definitions/shift_write_approval_view';
import { type PermissionStatus } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import { AssistantCardFrame } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_question_views';
import {
    type ApprovalEditor,
    type ApprovalPresentation,
    type AssignmentResult,
    type CaregiverTemplateValues,
    type CommunicationOutcome,
    type NoteResult,
    type PermissionOutcome,
    type SafeRenderContext,
    type WriteResultPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CardTransition } from '@apps/web/features/phoebe_chat_v3/ui/primitives/card_transition';
import {
    CardActionButton,
    CommunicationActions,
    CommunicationCard,
    CommunicationField,
    CommunicationFields,
    CommunicationReceipt,
    RecipientPill,
    VariableButton,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/communication_card';
import {
    CompactReceipt,
    type ReceiptSurface,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { GenericApprovalPanel } from '@apps/web/features/phoebe_chat_v3/ui/primitives/generic_approval_panel';
import { OutreachCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/outreach_card';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { SaveNoteCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/save_note_card';
import { SettingsApprovalPanel } from '@apps/web/features/phoebe_chat_v3/ui/primitives/settings_approval_panel';
import {
    caregiverTemplateValuesFromName,
    expandCaregiverTemplateTokens,
    normalizeCaregiverTemplateTokens,
    normalizeCaregiverTemplateTokensForRecipient,
    SMS_MESSAGE_VARIABLES,
    SmsMessageEditor,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/sms_message_editor';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export type { PermissionOutcome } from '@apps/web/features/phoebe_chat_v3/registry/types';

function permissionStatusCopy(status: PermissionStatus): string {
    switch (status) {
        case 'waiting':
            return 'Permission requested';
        case 'granted':
            return 'Allowed';
        case 'denied':
            return 'Denied';
        case 'expired':
            return 'Skipped';
        default: {
            const exhaustiveStatus: never = status;
            return exhaustiveStatus;
        }
    }
}

function pendingPermissionStatusCopy(
    status: PermissionStatus,
): string | undefined {
    return status === 'waiting' ? permissionStatusCopy(status) : undefined;
}

function LegacyControlledPermissionView({
    batchCallIds,
    callId,
    requestDecision,
    status,
    outcome,
    receipt,
}: Readonly<{
    readonly batchCallIds: ReadonlyArray<string> | null;
    readonly callId: string;
    readonly presentation: null;
    readonly requestDecision: SafeRenderContext['requestToolDecision'];
    readonly status: PermissionStatus;
    readonly outcome: PermissionOutcome;
    readonly receipt?: WriteResultPresentation;
}>): ReactNode {
    const [submitting, setSubmitting] = useState(false);

    function submitDecision(decision: 'deny' | 'allow_once'): void {
        if (submitting) {
            return;
        }
        if (batchCallIds !== null) {
            setSubmitting(true);
            void requestDecision({
                kind: 'tool_decision',
                targetKind: 'batch',
                callIds: batchCallIds,
                decision,
            }).then((outcome) => {
                if (outcome !== 'accepted' && outcome !== 'already_resolved') {
                    setSubmitting(false);
                }
            });
            return;
        }
        setSubmitting(true);
        void requestDecision({
            kind: 'tool_decision',
            targetKind: 'single',
            callId,
            decision,
        }).then((outcome) => {
            if (outcome !== 'accepted' && outcome !== 'already_resolved') {
                setSubmitting(false);
            }
        });
    }

    return (
        <AssistantCardFrame statusText={pendingPermissionStatusCopy(status)}>
            <GenericApprovalPanel
                outcome={outcome}
                receipt={receipt}
                title="Permission details unavailable"
                details="Phoebe requested permission, but the details could not be displayed safely."
                decision={
                    status === 'waiting'
                        ? 'pending'
                        : status === 'granted'
                          ? 'allowed'
                          : 'denied'
                }
                onAllow={() => submitDecision('allow_once')}
                onDeny={() => submitDecision('deny')}
                disabled={submitting}
            />
        </AssistantCardFrame>
    );
}

function presentationGroup(
    presentation: ApprovalPresentation,
    heading: string,
): ReadonlyArray<string> {
    return (
        presentation.groups.find((group) => group.heading === heading)?.items ??
        []
    );
}

function CommunicationCaregiverPicker({
    addLabel,
    deselectedRecipients,
    disabled,
    icon = 'add',
    onReselect,
    onSelect,
    scope,
    selectedCaregiverIds,
    closeOnSelect = false,
}: Readonly<{
    readonly addLabel: string;
    readonly deselectedRecipients: ReadonlyArray<SmsRecipient>;
    readonly disabled: boolean;
    /** "swap" marks a replace action (voice), "add" an append action (SMS). */
    readonly icon?: 'add' | 'swap';
    readonly onReselect: (recipient: SmsRecipient) => void;
    readonly onSelect: (caregiver: CommunicationCaregiver) => void;
    readonly scope: SafeRenderContext['scope'];
    readonly selectedCaregiverIds: ReadonlySet<string>;
    readonly closeOnSelect?: boolean;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { caregivers, failed, isLoading, retry } =
        useCommunicationCaregiverSearch({
            enabled: open,
            mode: scope.mode,
            organizationId: scope.organizationId,
            search,
        });
    const normalizedSearch = search.trim().toLowerCase();
    const matchingDeselectedRecipients = deselectedRecipients.filter(
        (recipient) =>
            normalizedSearch === '' ||
            recipient.name.toLowerCase().includes(normalizedSearch),
    );
    const deselectedCaregiverIds = new Set(
        deselectedRecipients
            .filter((recipient) => recipient.handle_type === 'caregiver_id')
            .map((recipient) => recipient.handle_id),
    );
    const availableCaregivers = caregivers.filter(
        (caregiver) =>
            !selectedCaregiverIds.has(caregiver.id) &&
            !deselectedCaregiverIds.has(caregiver.id),
    );

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setSearch('');
                }
            }}
        >
            <PopoverTrigger asChild={true}>
                <button
                    type="button"
                    disabled={disabled}
                    className="text-agent-action hover:bg-agent-action-wash text-v3-chat inline-flex min-h-6 items-center gap-1 rounded-md px-1.5 py-[3px] font-semibold disabled:opacity-[0.42]"
                >
                    {icon === 'swap' ? (
                        <ArrowRightLeft aria-hidden="true" className="size-4" />
                    ) : (
                        <span aria-hidden="true">+</span>
                    )}
                    {addLabel}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="border-border bg-background w-72 p-2 shadow-lg"
            >
                <Input
                    autoFocus={true}
                    aria-label="Search caregivers"
                    placeholder="Search caregivers"
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    className="text-v3-chat md:text-v3-chat h-9"
                />
                <div className="mt-2 max-h-56 overflow-y-auto">
                    {matchingDeselectedRecipients.map((recipient) => (
                        <button
                            key={`${recipient.handle_type}:${recipient.handle_id}`}
                            type="button"
                            aria-label={formatName(recipient.name)}
                            onClick={() => {
                                onReselect(recipient);
                                if (closeOnSelect) {
                                    setOpen(false);
                                    setSearch('');
                                }
                            }}
                            className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-2 text-left"
                        >
                            <span className="min-w-0 flex-1">
                                <strong className="text-v3-chat block truncate">
                                    {formatName(recipient.name)}
                                </strong>
                            </span>
                        </button>
                    ))}
                    {isLoading ? (
                        <div className="text-muted-foreground text-v3-chat flex items-center justify-center gap-2 py-5">
                            <Spinner size="sm" />
                            Loading caregivers
                        </div>
                    ) : failed && matchingDeselectedRecipients.length === 0 ? (
                        <div className="text-v3-chat flex flex-col items-center gap-2 px-2 py-4 text-center">
                            <p className="text-agent-negative">
                                Caregivers could not be loaded.
                            </p>
                            <button
                                type="button"
                                className="text-agent-action font-semibold"
                                onClick={retry}
                            >
                                Try again
                            </button>
                        </div>
                    ) : matchingDeselectedRecipients.length === 0 &&
                      availableCaregivers.length === 0 ? (
                        <p className="text-muted-foreground text-v3-chat px-2 py-4 text-center">
                            No caregivers found.
                        </p>
                    ) : (
                        availableCaregivers.map((caregiver) => (
                            <button
                                key={caregiver.id}
                                type="button"
                                aria-label={`${caregiver.name}${caregiver.officeName === null ? '' : ` ${caregiver.officeName}`}`}
                                onClick={() => {
                                    onSelect(caregiver);
                                    if (closeOnSelect) {
                                        setOpen(false);
                                        setSearch('');
                                    }
                                }}
                                className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-2 text-left"
                            >
                                <span className="min-w-0 flex-1">
                                    <strong className="text-v3-chat block truncate">
                                        {formatName(caregiver.name)}
                                    </strong>
                                    {caregiver.officeName !== null && (
                                        <span className="text-muted-foreground text-v3-chat-meta block truncate">
                                            {caregiver.officeName}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

type SmsEditor = Extract<ApprovalEditor, { readonly kind: 'sms' }>;
type SmsRecipient = SmsEditor['recipients'][number];
type VoiceEditor = Extract<ApprovalEditor, { readonly kind: 'voice' }>;
type VoiceRecipient = Pick<
    VoiceEditor,
    'handle_type' | 'handle_id' | 'name' | 'template_values'
>;

function templateValuesForCaregiver(
    caregiver: CommunicationCaregiver,
): CaregiverTemplateValues {
    const fallback = caregiverTemplateValuesFromName(caregiver.name);
    return {
        caregiver_first_name:
            caregiver.preferredName?.trim() ||
            caregiver.firstName?.trim() ||
            fallback.caregiver_first_name,
        caregiver_last_name:
            caregiver.lastName?.trim() || fallback.caregiver_last_name,
        caregiver_name: caregiver.name.trim().replace(/\s+/g, ' '),
    };
}

/**
 * One-line title for an approval collapsed inside the approval stack. Names
 * the action and its people so a coordinator can size up the pending set
 * without expanding each card.
 */
export function collapsedApprovalTitle(
    editor: ApprovalEditor | null,
    presentation: ApprovalPresentation | null,
    formatName: (name: string | null | undefined) => string,
): string {
    switch (editor?.kind) {
        case 'sms': {
            const names = editor.recipients.map((recipient) =>
                formatName(recipient.name),
            );
            const first = names[0];
            if (first === undefined) {
                return 'Send a text';
            }
            if (names.length === 1) {
                return `Text ${first}`;
            }
            if (names.length === 2) {
                return `Text ${first} and ${names[1]}`;
            }
            return `Text ${first} and ${names.length - 1} others`;
        }
        case 'voice':
            return `Call ${formatName(editor.name)}`;
        case 'assign_fill':
            return `Assign ${formatName(editor.caregiver_name)}`;
        case 'outreach':
            return 'Start an outreach';
        case 'shift_write':
            return editor.card.summary;
        case 'settings':
            return editor.title;
        case 'ehr_person_note':
            return presentation?.question ?? 'Add a note';
        case 'save_note':
            return presentation?.question ?? 'Save a note';
        case undefined:
            return presentation?.question ?? 'Approval needed';
        default: {
            const exhaustiveEditor: never = editor;
            return exhaustiveEditor;
        }
    }
}

export function NoApprovalCommunicationView({
    approvalReason,
    editor,
    executionOutcome,
    communicationOutcome,
}: Readonly<{
    readonly approvalReason?: string;
    readonly editor: Extract<
        ApprovalEditor,
        { readonly kind: 'sms' | 'voice' }
    >;
    readonly executionOutcome: PermissionOutcome;
    readonly communicationOutcome?: CommunicationOutcome;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const [pageIndex, setPageIndex] = useState(0);
    const smsRecipient =
        editor.kind === 'sms' ? (editor.recipients[pageIndex] ?? null) : null;
    const recipientNames =
        editor.kind === 'sms'
            ? editor.recipients.map((recipient) => formatName(recipient.name))
            : [formatName(editor.name)];
    const receipt = communicationReceiptCopy(
        editor.kind,
        communicationReceiptState(
            { kind: 'no_approval', executionOutcome },
            communicationOutcome,
        ),
        recipientNames.length,
    );

    const fields = (
        <CommunicationFields>
            <CommunicationField label="To:" inline={true}>
                {recipientNames.map((name, index) => (
                    <RecipientPill key={`${index}:${name}`} name={name} />
                ))}
            </CommunicationField>
            {editor.kind === 'sms' && smsRecipient !== null ? (
                <CommunicationField
                    label={
                        editor.recipients.length > 1
                            ? `Message for ${formatName(smsRecipient.name)}:`
                            : 'Message:'
                    }
                >
                    <SmsMessageEditor
                        ariaLabel="Message"
                        value={expandCaregiverTemplateTokens(
                            smsRecipient.message,
                            smsRecipient.template_values ?? smsRecipient.name,
                        )}
                    />
                    {editor.recipients.length > 1 && (
                        <div className="mt-2 flex items-center justify-center gap-2">
                            <button
                                type="button"
                                aria-label="Previous person"
                                disabled={pageIndex === 0}
                                onClick={() =>
                                    setPageIndex((current) =>
                                        Math.max(0, current - 1),
                                    )
                                }
                                className="bg-card text-foreground/80 inline-flex size-7 items-center justify-center rounded-full border disabled:opacity-35"
                            >
                                ‹
                            </button>
                            <span className="text-muted-foreground text-v3-chat-meta">
                                {pageIndex + 1} of {editor.recipients.length}
                            </span>
                            <button
                                type="button"
                                aria-label="Next person"
                                disabled={
                                    pageIndex === editor.recipients.length - 1
                                }
                                onClick={() =>
                                    setPageIndex((current) =>
                                        Math.min(
                                            editor.recipients.length - 1,
                                            current + 1,
                                        ),
                                    )
                                }
                                className="bg-card text-foreground/80 inline-flex size-7 items-center justify-center rounded-full border disabled:opacity-35"
                            >
                                ›
                            </button>
                        </div>
                    )}
                </CommunicationField>
            ) : editor.kind === 'voice' ? (
                <Fragment>
                    <CommunicationField label="Purpose:">
                        <SmsMessageEditor
                            ariaLabel="Call purpose"
                            value={expandCaregiverTemplateTokens(
                                editor.instructions,
                                editor.template_values ?? editor.name,
                            )}
                        />
                    </CommunicationField>
                    {editor.opening_line !== null && (
                        <CommunicationField label="Opening line:">
                            <SmsMessageEditor
                                ariaLabel="Opening line"
                                value={expandCaregiverTemplateTokens(
                                    editor.opening_line,
                                    editor.template_values ?? editor.name,
                                )}
                            />
                        </CommunicationField>
                    )}
                </Fragment>
            ) : null}
        </CommunicationFields>
    );
    return (
        <CardTransition
            transitionKey={receipt === null ? 'active' : receipt.status}
        >
            <AssistantCardFrame>
                <CommunicationCard tone={receipt?.tone ?? 'editing'}>
                    {receipt === null ? (
                        fields
                    ) : (
                        <Fragment>
                            <CompactReceipt
                                embedded={true}
                                surface="communication"
                                status={receipt.status}
                                title={receipt.title}
                                summary={
                                    <Fragment>
                                        <div>
                                            <ResolvedReferenceText
                                                text={recipientNames.join(', ')}
                                            />
                                        </div>
                                        {receipt.detail !== undefined && (
                                            <div className="mt-1">
                                                {receipt.detail}
                                            </div>
                                        )}
                                    </Fragment>
                                }
                            />
                            {approvalReason !== undefined ? (
                                <p className="text-muted-foreground px-3.5 pb-2 text-xs">
                                    Didn’t ask because: {approvalReason}
                                </p>
                            ) : null}
                            <details className="text-v3-chat px-3.5 pb-3">
                                <summary className="text-muted-foreground cursor-pointer rounded focus-visible:ring-2">
                                    {editor.kind === 'sms'
                                        ? 'View message'
                                        : 'View call details'}
                                </summary>
                                {fields}
                            </details>
                        </Fragment>
                    )}
                </CommunicationCard>
            </AssistantCardFrame>
        </CardTransition>
    );
}

function CommunicationApprovalView({
    approvalReason,
    editor,
    kind,
    presentation,
    status,
    permissionOutcome,
    communicationOutcome,
    submitting,
    onAllow,
    onDeny,
    scope,
}: Readonly<{
    readonly approvalReason?: string;
    readonly kind: 'sms' | 'voice';
    readonly editor: Extract<
        ApprovalEditor,
        { readonly kind: 'sms' | 'voice' }
    > | null;
    readonly presentation: ApprovalPresentation;
    readonly status: PermissionStatus;
    readonly permissionOutcome: PermissionOutcome;
    readonly communicationOutcome?: CommunicationOutcome;
    readonly submitting: boolean;
    readonly onAllow: (argumentsOverride?: Record<string, unknown>) => void;
    readonly onDeny: () => void;
    readonly scope: SafeRenderContext['scope'];
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const [activeRecipientIds, setActiveRecipientIds] = useState<
        ReadonlySet<string>
    >(
        () =>
            new Set(
                editor?.kind === 'sms'
                    ? editor.recipients.map((recipient) => recipient.handle_id)
                    : editor === null
                      ? []
                      : [editor.handle_id],
            ),
    );
    const [smsTemplate, setSmsTemplate] = useState(() => {
        const firstRecipient =
            editor?.kind === 'sms' ? editor.recipients[0] : undefined;
        return firstRecipient === undefined
            ? ''
            : normalizeCaregiverTemplateTokensForRecipient(
                  firstRecipient.message,
                  firstRecipient.template_values ?? firstRecipient.name,
              );
    });
    const [messages, setMessages] = useState<Readonly<Record<string, string>>>(
        (): Readonly<Record<string, string>> =>
            editor?.kind === 'voice'
                ? {
                      instructions:
                          normalizeCaregiverTemplateTokensForRecipient(
                              editor.instructions,
                              editor.template_values ?? editor.name,
                          ),
                      opening_line:
                          normalizeCaregiverTemplateTokensForRecipient(
                              editor.opening_line ?? '',
                              editor.template_values ?? editor.name,
                          ),
                  }
                : {},
    );
    const messageEditorActions = useRef<VariableTextEditorActions>(null);
    const openingLineEditorActions = useRef<VariableTextEditorActions>(null);
    const [addedSmsRecipients, setAddedSmsRecipients] = useState<
        ReadonlyArray<SmsRecipient>
    >([]);
    // place_voice_call dials exactly one person, so the card holds a single
    // recipient and picking someone else replaces them.
    const [voiceRecipient, setVoiceRecipient] = useState<VoiceRecipient | null>(
        () =>
            editor?.kind === 'voice'
                ? {
                      handle_type: editor.handle_type,
                      handle_id: editor.handle_id,
                      name: editor.name,
                  }
                : null,
    );
    const waiting = status === 'waiting';
    const recipients = presentationGroup(
        presentation,
        kind === 'sms' ? 'Recipients' : 'Recipient',
    );
    const sharedMessage = presentationGroup(presentation, 'Message')[0];
    const recipientMessageGroups = presentation.groups.filter((group) =>
        group.heading.startsWith('Message for '),
    );
    const editableRecipients =
        editor?.kind === 'sms'
            ? [...editor.recipients, ...addedSmsRecipients]
            : [];
    const activeRecipients = editableRecipients.filter((recipient) =>
        activeRecipientIds.has(recipient.handle_id),
    );
    const firstRecipient = activeRecipients[0] ?? null;
    const recipientCount =
        editor?.kind === 'sms'
            ? activeRecipients.length
            : editor?.kind === 'voice'
              ? voiceRecipient === null
                  ? 0
                  : 1
              : recipients.length;
    const receipt = communicationReceiptCopy(
        kind,
        communicationReceiptState(
            { kind: 'approval', status, permissionOutcome },
            communicationOutcome,
        ),
        recipientCount,
    );
    const displayedRecipientNames =
        editor?.kind === 'sms'
            ? activeRecipients.map((recipient) => formatName(recipient.name))
            : editor?.kind === 'voice'
              ? voiceRecipient === null
                  ? []
                  : [formatName(voiceRecipient.name)]
              : recipients;
    const currentMessage =
        editor?.kind === 'sms'
            ? smsTemplate
            : normalizeCaregiverTemplateTokens(sharedMessage ?? '');
    // Approving must send exactly what the preview showed, so a message
    // whose final text differs from the staged arguments — including a
    // caregiver token that expands per recipient — routes through an
    // arguments override instead of the untouched-approval path.
    const finalSmsContent = (recipient: SmsRecipient): string =>
        expandCaregiverTemplateTokens(
            smsTemplate,
            recipient.template_values ?? recipient.name,
        );
    const voiceInstructions =
        messages['instructions'] ??
        (editor?.kind === 'voice' ? editor.instructions : '');
    const voiceOpeningLine = messages['opening_line'] ?? '';
    const finalVoiceInstructions =
        editor?.kind === 'voice' && voiceRecipient !== null
            ? expandCaregiverTemplateTokens(
                  voiceInstructions,
                  voiceRecipient.template_values ?? voiceRecipient.name,
              )
            : voiceInstructions;
    const finalVoiceOpeningLine =
        editor?.kind === 'voice' && voiceRecipient !== null
            ? expandCaregiverTemplateTokens(
                  voiceOpeningLine,
                  voiceRecipient.template_values ?? voiceRecipient.name,
              )
            : voiceOpeningLine;
    const initialVoiceInstructions =
        editor?.kind === 'voice'
            ? normalizeCaregiverTemplateTokensForRecipient(
                  editor.instructions,
                  editor.template_values ?? editor.name,
              )
            : '';
    const initialVoiceOpeningLine =
        editor?.kind === 'voice'
            ? normalizeCaregiverTemplateTokensForRecipient(
                  editor.opening_line ?? '',
                  editor.template_values ?? editor.name,
              )
            : '';
    const editorEdited =
        editor?.kind === 'sms'
            ? activeRecipients.length !== editor.recipients.length ||
              addedSmsRecipients.length > 0 ||
              editor.recipients.some(
                  (recipient) =>
                      finalSmsContent(recipient) !== recipient.message,
              )
            : editor?.kind === 'voice'
              ? voiceRecipient?.handle_type !== editor.handle_type ||
                voiceRecipient?.handle_id !== editor.handle_id ||
                voiceRecipient?.name !== editor.name ||
                voiceInstructions !== initialVoiceInstructions ||
                voiceOpeningLine !== initialVoiceOpeningLine
              : false;
    const selectedCaregiverIds = new Set(
        activeRecipients
            .filter((recipient) => recipient.handle_type === 'caregiver_id')
            .map((recipient) => recipient.handle_id),
    );
    const voiceSelectedCaregiverIds = new Set(
        voiceRecipient !== null && voiceRecipient.handle_type === 'caregiver_id'
            ? [voiceRecipient.handle_id]
            : [],
    );
    const messageIssueId = useId();
    const openingLineIssueId = useId();
    // Each SMS is checked as it ships, with caregiver tokens expanded,
    // because that final text is what the server measures.
    const smsDrafts = activeRecipients.map((recipient) => ({
        recipient,
        issue: draftIssue(finalSmsContent(recipient), {
            limit: SMS_MESSAGE_CHARACTER_LIMIT,
        }),
    }));
    const messageIssue =
        editor?.kind === 'sms'
            ? (smsDrafts.find((draft) => draft.recipient === firstRecipient)
                  ?.issue ?? null)
            : editor?.kind === 'voice'
              ? draftIssue(finalVoiceInstructions, {
                    limit: VOICE_INSTRUCTIONS_CHARACTER_LIMIT,
                })
              : null;
    // Another recipient can hold the approval back; name them so
    // the disabled button is never unexplained.
    const otherRecipientDraft =
        messageIssue === null
            ? smsDrafts.find((draft) => draft.issue !== null)
            : undefined;
    const openingLineIssue =
        editor?.kind === 'voice'
            ? draftIssue(finalVoiceOpeningLine, {
                  limit: VOICE_OPENING_LINE_CHARACTER_LIMIT,
                  required: false,
              })
            : null;
    const hasValidDraft =
        editor?.kind === 'sms'
            ? smsDrafts.length > 0 &&
              smsDrafts.every((draft) => draft.issue === null)
            : editor?.kind === 'voice'
              ? voiceRecipient !== null &&
                messageIssue === null &&
                openingLineIssue === null
              : true;

    // The decision endpoint measures these strings untrimmed, so ship the
    // trimmed text the card measured rather than the raw editor value.
    function editedArgumentsOverride(): Record<string, unknown> | undefined {
        if (!editorEdited) {
            return undefined;
        }
        if (editor?.kind === 'sms') {
            return {
                recipients: activeRecipients.map((recipient) => ({
                    [recipient.handle_type]: recipient.handle_id,
                    name: recipient.name,
                    content: finalSmsContent(recipient).trim(),
                })),
                content: null,
            };
        }
        if (editor?.kind === 'voice' && voiceRecipient !== null) {
            return {
                [voiceRecipient.handle_type]: voiceRecipient.handle_id,
                name: voiceRecipient.name,
                instructions: finalVoiceInstructions.trim(),
                opening_line: finalVoiceOpeningLine.trim() || null,
            };
        }
        return undefined;
    }

    function allowEditedAction(): void {
        if (
            editorEdited &&
            editor?.kind === 'voice' &&
            voiceRecipient === null
        ) {
            return;
        }
        onAllow(editedArgumentsOverride());
    }

    const caregiverPicker =
        editor === null ? null : (
            <CommunicationCaregiverPicker
                addLabel={
                    editor.kind === 'sms'
                        ? 'Add people'
                        : voiceRecipient === null
                          ? 'Add person'
                          : 'Change person'
                }
                deselectedRecipients={
                    editor.kind === 'sms'
                        ? editableRecipients.filter(
                              (recipient) =>
                                  !activeRecipientIds.has(recipient.handle_id),
                          )
                        : []
                }
                icon={
                    editor.kind === 'voice' && voiceRecipient !== null
                        ? 'swap'
                        : 'add'
                }
                closeOnSelect={editor.kind === 'voice'}
                disabled={submitting}
                onReselect={(recipient) => {
                    setActiveRecipientIds(
                        (current) => new Set([...current, recipient.handle_id]),
                    );
                }}
                scope={scope}
                selectedCaregiverIds={
                    editor.kind === 'sms'
                        ? selectedCaregiverIds
                        : voiceSelectedCaregiverIds
                }
                onSelect={(caregiver) => {
                    if (editor.kind === 'sms') {
                        setAddedSmsRecipients((current) => [
                            ...current,
                            {
                                handle_type: 'caregiver_id',
                                handle_id: caregiver.id,
                                name: caregiver.name,
                                message: smsTemplate,
                                template_values:
                                    templateValuesForCaregiver(caregiver),
                            },
                        ]);
                        setActiveRecipientIds(
                            (current) => new Set([...current, caregiver.id]),
                        );
                        return;
                    }
                    // Selecting replaces the current person: one call, one
                    // recipient.
                    setVoiceRecipient({
                        handle_type: 'caregiver_id',
                        handle_id: caregiver.id,
                        name: caregiver.name,
                        template_values: templateValuesForCaregiver(caregiver),
                    });
                }}
            />
        );

    return (
        <AssistantCardFrame statusText={pendingPermissionStatusCopy(status)}>
            <div className="flex flex-col gap-2">
                <CommunicationCard tone={receipt?.tone ?? 'editing'}>
                    {waiting ? (
                        <Fragment>
                            <div className="border-border/60 border-b px-3.5 py-3">
                                <h3 className="text-v3-chat m-0 font-semibold">
                                    <ResolvedReferenceText
                                        text={presentation.question}
                                    />
                                </h3>
                                {approvalReason !== undefined ? (
                                    <p className="text-muted-foreground text-v3-chat-meta mt-1">
                                        Asked because: {approvalReason}
                                    </p>
                                ) : null}
                            </div>
                            <CommunicationFields>
                                <CommunicationField label="To:" inline={true}>
                                    {displayedRecipientNames.map(
                                        (recipient, index) => (
                                            <RecipientPill
                                                key={`${index}:${recipient}`}
                                                name={recipient}
                                                onRemove={
                                                    editor?.kind === 'sms'
                                                        ? () => {
                                                              const removed =
                                                                  activeRecipients[
                                                                      index
                                                                  ];
                                                              if (
                                                                  removed ===
                                                                  undefined
                                                              ) {
                                                                  return;
                                                              }
                                                              setActiveRecipientIds(
                                                                  (current) => {
                                                                      const next =
                                                                          new Set(
                                                                              current,
                                                                          );
                                                                      next.delete(
                                                                          removed.handle_id,
                                                                      );
                                                                      return next;
                                                                  },
                                                              );
                                                          }
                                                        : editor?.kind ===
                                                            'voice'
                                                          ? () => {
                                                                setVoiceRecipient(
                                                                    null,
                                                                );
                                                                // The opening line addressed them.
                                                                setMessages(
                                                                    (
                                                                        current,
                                                                    ) => ({
                                                                        ...current,
                                                                        opening_line:
                                                                            '',
                                                                    }),
                                                                );
                                                            }
                                                          : undefined
                                                }
                                            />
                                        ),
                                    )}
                                    {caregiverPicker}
                                </CommunicationField>
                                <CommunicationField
                                    label={
                                        kind === 'sms'
                                            ? 'Message template:'
                                            : 'Purpose:'
                                    }
                                >
                                    {kind === 'sms' &&
                                    editor === null &&
                                    recipientMessageGroups.length > 0 ? (
                                        <div className="space-y-3">
                                            {recipientMessageGroups.map(
                                                (group, index) => (
                                                    <CommunicationField
                                                        key={index}
                                                        label={group.heading}
                                                    >
                                                        <SmsMessageEditor
                                                            ariaLabel={
                                                                group.heading
                                                            }
                                                            value={
                                                                group
                                                                    .items[0] ??
                                                                ''
                                                            }
                                                        />
                                                    </CommunicationField>
                                                ),
                                            )}
                                        </div>
                                    ) : kind === 'sms' ? (
                                        <SmsMessageEditor
                                            ariaLabel="Message"
                                            removePillsOnDragOut={true}
                                            snapVariableDropsToWords={true}
                                            describedBy={
                                                messageIssue === null
                                                    ? undefined
                                                    : messageIssueId
                                            }
                                            value={currentMessage}
                                            onChange={
                                                editor?.kind === 'sms' &&
                                                firstRecipient !== null
                                                    ? setSmsTemplate
                                                    : undefined
                                            }
                                            actionRef={messageEditorActions}
                                        />
                                    ) : (
                                        <SmsMessageEditor
                                            ariaLabel="Call purpose"
                                            value={
                                                messages['instructions'] ??
                                                presentationGroup(
                                                    presentation,
                                                    'Purpose',
                                                )[0] ??
                                                ''
                                            }
                                            describedBy={
                                                messageIssue === null
                                                    ? undefined
                                                    : messageIssueId
                                            }
                                            onChange={
                                                editor === null
                                                    ? undefined
                                                    : (value) =>
                                                          setMessages(
                                                              (current) => ({
                                                                  ...current,
                                                                  instructions:
                                                                      value,
                                                              }),
                                                          )
                                            }
                                            actionRef={messageEditorActions}
                                        />
                                    )}
                                    {messageIssue !== null && (
                                        <p
                                            id={messageIssueId}
                                            className="text-muted-foreground text-v3-chat-meta mt-1"
                                        >
                                            {draftIssueCopy(
                                                messageIssue,
                                                kind === 'sms'
                                                    ? 'Message'
                                                    : 'Call purpose',
                                            )}
                                        </p>
                                    )}
                                    {otherRecipientDraft !== undefined &&
                                        otherRecipientDraft.issue !== null && (
                                            <p className="text-muted-foreground text-v3-chat-meta mt-1">
                                                {otherRecipientDraft.issue
                                                    .kind === 'empty'
                                                    ? draftIssueCopy(
                                                          otherRecipientDraft.issue,
                                                          `Message for ${formatName(otherRecipientDraft.recipient.name)}`,
                                                      )
                                                    : `Message for ${formatName(otherRecipientDraft.recipient.name)}: ${draftIssueCopy(otherRecipientDraft.issue, 'Message')}`}
                                            </p>
                                        )}
                                    {editor !== null &&
                                        (kind === 'voice' ||
                                            firstRecipient !== null) && (
                                            <div
                                                className="mt-[9px] flex flex-wrap items-center gap-1.5"
                                                aria-label={
                                                    kind === 'sms'
                                                        ? 'Message variables'
                                                        : 'Purpose variables'
                                                }
                                            >
                                                {SMS_MESSAGE_VARIABLES.map(
                                                    (variable) => (
                                                        <VariableButton
                                                            key={variable.token}
                                                            variable={`{${variable.token}}`}
                                                            onClick={() =>
                                                                messageEditorActions.current?.insertVariable(
                                                                    variable.token,
                                                                )
                                                            }
                                                        >
                                                            {variable.label}
                                                        </VariableButton>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                </CommunicationField>
                                {kind === 'voice' &&
                                    (editor?.kind === 'voice' ||
                                        presentationGroup(
                                            presentation,
                                            'Opening line',
                                        )[0] !== undefined) && (
                                        <CommunicationField label="Opening line:">
                                            <SmsMessageEditor
                                                ariaLabel="Opening line"
                                                value={
                                                    messages['opening_line'] ??
                                                    presentationGroup(
                                                        presentation,
                                                        'Opening line',
                                                    )[0] ??
                                                    ''
                                                }
                                                describedBy={
                                                    openingLineIssue === null
                                                        ? undefined
                                                        : openingLineIssueId
                                                }
                                                onChange={
                                                    editor?.kind === 'voice'
                                                        ? (value) =>
                                                              setMessages(
                                                                  (
                                                                      current,
                                                                  ) => ({
                                                                      ...current,
                                                                      opening_line:
                                                                          value,
                                                                  }),
                                                              )
                                                        : undefined
                                                }
                                                actionRef={
                                                    openingLineEditorActions
                                                }
                                            />
                                            {openingLineIssue !== null && (
                                                <p
                                                    id={openingLineIssueId}
                                                    className="text-muted-foreground text-v3-chat-meta mt-1"
                                                >
                                                    {draftIssueCopy(
                                                        openingLineIssue,
                                                        'Opening line',
                                                    )}
                                                </p>
                                            )}
                                            {editor?.kind === 'voice' && (
                                                <div
                                                    className="mt-[9px] flex flex-wrap items-center gap-1.5"
                                                    aria-label="Opening line variables"
                                                >
                                                    {SMS_MESSAGE_VARIABLES.map(
                                                        (variable) => (
                                                            <VariableButton
                                                                key={
                                                                    variable.token
                                                                }
                                                                variable={`{${variable.token}}`}
                                                                onClick={() =>
                                                                    openingLineEditorActions.current?.insertVariable(
                                                                        variable.token,
                                                                    )
                                                                }
                                                            >
                                                                {variable.label}
                                                            </VariableButton>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </CommunicationField>
                                    )}
                            </CommunicationFields>
                            <CommunicationActions>
                                <CardActionButton
                                    variant="secondary"
                                    disabled={submitting}
                                    onClick={onDeny}
                                >
                                    Cancel
                                </CardActionButton>
                                <CardActionButton
                                    variant="primary"
                                    onClick={allowEditedAction}
                                    disabled={submitting || !hasValidDraft}
                                >
                                    {kind === 'sms'
                                        ? recipientCount > 1
                                            ? 'Approve all & send'
                                            : 'Approve & send'
                                        : 'Call caregiver'}
                                </CardActionButton>
                            </CommunicationActions>
                        </Fragment>
                    ) : receipt !== null ? (
                        <CommunicationReceipt
                            {...receipt}
                            title={
                                receipt.statusLabel === 'Approved' ||
                                receipt.statusLabel === 'Rejected'
                                    ? `${kind === 'sms' ? 'Text' : 'Call'} ${displayedRecipientNames.join(', ')}`
                                    : receipt.title
                            }
                        />
                    ) : null}
                </CommunicationCard>
            </div>
        </AssistantCardFrame>
    );
}

const APPROVAL_RECEIPT_SURFACES: Record<
    ApprovalRenderer['kind'],
    ReceiptSurface
> = {
    settings: 'action',
    shift_write: 'action',
    ehr_person_note: 'action',
    legacy: 'action',
    communication: 'communication',
    assignment: 'assignment',
    legacy_assignment: 'assignment',
    outreach: 'outreach',
    save_note: 'action',
    generic: 'action',
};

function PermissionView({
    approvalReason,
    batchCallIds,
    callId,
    editor,
    presentation,
    requestDecision,
    scope,
    status,
    outcome = 'pending',
    assignmentResult,
    communicationOutcome,
    noteResult,
    receipt,
}: Readonly<{
    readonly approvalReason?: string;
    readonly batchCallIds: ReadonlyArray<string> | null;
    readonly callId: string;
    readonly editor: ApprovalEditor | null;
    readonly presentation: ApprovalPresentation | null;
    readonly requestDecision: SafeRenderContext['requestToolDecision'];
    readonly scope: SafeRenderContext['scope'];
    readonly status: PermissionStatus;
    /** What the tool did after a grant; the receipt only claims a send once it succeeded. */
    readonly outcome?: PermissionOutcome;
    readonly assignmentResult?: AssignmentResult;
    readonly communicationOutcome?: CommunicationOutcome;
    readonly noteResult?: NoteResult;
    readonly receipt?: WriteResultPresentation;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const [submitting, setSubmitting] = useState(false);
    const renderer = approvalRenderer(
        editor,
        presentation,
        batchCallIds !== null,
    );

    function submitDecision(
        decision: 'deny' | 'allow_once',
        argumentsOverride?: Record<string, unknown>,
    ): void {
        if (submitting || status !== 'waiting') {
            return;
        }
        if (batchCallIds !== null) {
            setSubmitting(true);
            void requestDecision({
                kind: 'tool_decision',
                targetKind: 'batch',
                callIds: batchCallIds,
                decision,
            }).then((outcome) => {
                if (outcome !== 'accepted' && outcome !== 'already_resolved') {
                    setSubmitting(false);
                }
            });
            return;
        }
        setSubmitting(true);
        void requestDecision({
            kind: 'tool_decision',
            targetKind: 'single',
            callId,
            decision,
            ...(argumentsOverride === undefined ? {} : { argumentsOverride }),
        }).then((submitOutcome) => {
            if (
                submitOutcome !== 'accepted' &&
                submitOutcome !== 'already_resolved'
            ) {
                setSubmitting(false);
            }
        });
    }

    if (status === 'expired') {
        // A new message retired this request. One receipt for every kind of
        // approval: the per-kind cards only know how to draw a decision. The
        // card reads "Skipped" with no explanation line underneath.
        return (
            <AssistantCardFrame statusText={permissionStatusCopy(status)}>
                <CompactReceipt
                    status="skipped"
                    surface={APPROVAL_RECEIPT_SURFACES[renderer.kind]}
                    title={
                        presentation?.question ??
                        collapsedApprovalTitle(editor, presentation, formatName)
                    }
                    ariaLabel="Skipped request"
                />
            </AssistantCardFrame>
        );
    }

    if (renderer.kind === 'shift_write') {
        return (
            <ShiftWriteApprovalView
                editor={renderer.editor}
                organizationId={scope.organizationId}
                outcome={outcome}
                receipt={receipt}
                status={status}
                submitting={submitting}
                onAllow={(argumentsOverride) =>
                    submitDecision('allow_once', argumentsOverride)
                }
                onDeny={() => submitDecision('deny')}
            />
        );
    }

    if (renderer.kind === 'settings') {
        return (
            <SettingsApprovalPanel
                editor={renderer.editor}
                decision={
                    status === 'waiting'
                        ? 'pending'
                        : status === 'granted'
                          ? 'allowed'
                          : 'denied'
                }
                outcome={outcome}
                receipt={receipt}
                disabled={submitting}
                onSave={(fields) => submitDecision('allow_once', { fields })}
                onCancel={() => submitDecision('deny')}
            />
        );
    }

    if (renderer.kind === 'ehr_person_note') {
        return (
            <EhrPersonNoteApprovalView
                editor={renderer.editor}
                organizationId={scope.organizationId}
                outcome={outcome}
                receipt={receipt}
                status={status}
                submitting={submitting}
                onAllow={(argumentsOverride) =>
                    submitDecision('allow_once', argumentsOverride)
                }
                onDeny={() => submitDecision('deny')}
            />
        );
    }

    if (presentation === null) {
        return (
            <LegacyControlledPermissionView
                batchCallIds={batchCallIds}
                callId={callId}
                presentation={presentation}
                outcome={outcome}
                receipt={receipt}
                requestDecision={requestDecision}
                status={status}
            />
        );
    }

    if (renderer.kind === 'communication') {
        return (
            <CommunicationApprovalView
                approvalReason={approvalReason}
                editor={
                    batchCallIds === null &&
                    (editor?.kind === 'sms' || editor?.kind === 'voice') &&
                    editor.kind === renderer.channel
                        ? editor
                        : null
                }
                kind={renderer.channel}
                presentation={presentation}
                scope={scope}
                status={status}
                permissionOutcome={outcome}
                communicationOutcome={communicationOutcome}
                submitting={submitting}
                onAllow={(argumentsOverride) =>
                    submitDecision('allow_once', argumentsOverride)
                }
                onDeny={() => submitDecision('deny')}
            />
        );
    }
    if (renderer.kind === 'assignment') {
        return (
            <AssignApprovalView
                statusText={pendingPermissionStatusCopy(status)}
                editor={renderer.editor}
                status={status}
                outcome={outcome}
                assignmentResult={assignmentResult}
                submitting={submitting}
                onAllow={(argumentsOverride) =>
                    submitDecision('allow_once', argumentsOverride)
                }
                onDeny={() => submitDecision('deny')}
            />
        );
    }

    if (renderer.kind === 'legacy_assignment') {
        return (
            <LegacyAssignmentApprovalView
                caregiverName={renderer.caregiverName}
                shifts={renderer.shifts}
                status={status}
                outcome={outcome}
                assignmentResult={assignmentResult}
                submitting={submitting}
                statusText={pendingPermissionStatusCopy(status)}
                onAllow={() => submitDecision('allow_once')}
                onDeny={() => submitDecision('deny')}
            />
        );
    }

    if (renderer.kind === 'outreach') {
        const shiftCount = presentation.groups.reduce(
            (count, group) => count + group.items.length,
            0,
        );
        const clientCount = presentation.groups.filter((group) =>
            group.heading.startsWith('Client: '),
        ).length;
        return (
            <AssistantCardFrame
                statusText={pendingPermissionStatusCopy(status)}
            >
                <div className="flex flex-col gap-2">
                    <OutreachCard
                        title={
                            status === 'granted'
                                ? 'Outreach started'
                                : 'Weekly coverage outreach'
                        }
                        detail={
                            editor?.kind === 'outreach'
                                ? `${editor.shift_count} ${editor.shift_count === 1 ? 'shift' : 'shifts'} · ${editor.client_count} ${editor.client_count === 1 ? 'client' : 'clients'} · ${editor.date_range}`
                                : `${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'} · ${clientCount} ${clientCount === 1 ? 'client' : 'clients'}`
                        }
                        stateLabel={
                            status === 'granted'
                                ? 'Preparing'
                                : status === 'denied'
                                  ? 'Denied'
                                  : undefined
                        }
                        broadcasting={submitting}
                        receipt={status === 'granted'}
                        action={
                            status === 'waiting'
                                ? {
                                      label: 'Start outreach',
                                      onAction: () =>
                                          submitDecision('allow_once'),
                                  }
                                : undefined
                        }
                        disabled={submitting}
                    />
                </div>
            </AssistantCardFrame>
        );
    }

    if (renderer.kind === 'save_note') {
        return (
            <AssistantCardFrame
                statusText={pendingPermissionStatusCopy(status)}
            >
                <SaveNoteCard
                    noteResult={noteResult}
                    presentation={presentation}
                    decision={
                        status === 'waiting'
                            ? 'pending'
                            : status === 'granted'
                              ? 'allowed'
                              : 'denied'
                    }
                    outcome={outcome}
                    disabled={submitting}
                    onSave={() => submitDecision('allow_once')}
                    onCancel={() => submitDecision('deny')}
                />
            </AssistantCardFrame>
        );
    }

    return (
        <AssistantCardFrame statusText={pendingPermissionStatusCopy(status)}>
            <GenericApprovalPanel
                title={presentation.question}
                outcome={outcome}
                receipt={receipt}
                decision={
                    status === 'waiting'
                        ? 'pending'
                        : status === 'granted'
                          ? 'allowed'
                          : 'denied'
                }
                groups={presentation.groups}
                onAllow={() => submitDecision('allow_once')}
                onDeny={() => submitDecision('deny')}
                disabled={submitting}
            />
        </AssistantCardFrame>
    );
}

export function ControlledPermissionView({
    approvalReason,
    ...props
}: Parameters<typeof PermissionView>[0] &
    Readonly<{ readonly approvalReason?: string }>): ReactNode {
    const reasonInCommunicationCard =
        approvalReason !== undefined &&
        props.status === 'waiting' &&
        approvalRenderer(
            props.editor,
            props.presentation,
            props.batchCallIds !== null,
        ).kind === 'communication';
    return (
        <CardTransition
            transitionKey={`${props.status}:${props.outcome ?? 'pending'}`}
        >
            <div className="space-y-1.5">
                <PermissionView
                    {...props}
                    approvalReason={
                        reasonInCommunicationCard ? approvalReason : undefined
                    }
                />
                {approvalReason !== undefined && !reasonInCommunicationCard ? (
                    <p className="text-muted-foreground px-1 text-xs">
                        Asked because: {approvalReason}
                    </p>
                ) : null}
            </div>
        </CardTransition>
    );
}
